import type BackOfficeConnector from "../data/backOfficeConnector";
import type { IPagination } from "../data/types";
import ChargeOrderSearch from "entities/chargeOrderSearchResponse";
import UnpairedPayment from "entities/unpairedPayment";
import type PaymentFilter from "entities/filters/paymentFilter";
import type ChargeOrderSearchFilter from "entities/filters/chargeOrderSearchFilter";
import type EventBus from "../services/eventBus";
import { excludeEmptyValues, serializeFilters } from "../utils/filters";
import RepositoryBase from "./repositoryBase";

export default class PaymentsPairingRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAllUnpairedPayments(pagination: IPagination, filters: PaymentFilter) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("unpaired_payment_search")
          .postPagedEntities<UnpairedPayment>({ ...serializeFilters(filters, false), ...pagination }, UnpairedPayment),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.2" }) }
    );
  }

  getUnpairedPayment(paymentId: string) {
    return this.callApi(api => api.all("call_center").one("unpaired_payments", paymentId).getEntity(UnpairedPayment));
  }

  getAllChargeOrderSearch(pagination: IPagination, filters: ChargeOrderSearchFilter, abortSignal: AbortSignal) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("charge_order_search")
          .postPagedEntities<ChargeOrderSearch>(
            { ...excludeEmptyValues(filters), ...pagination },
            ChargeOrderSearch,
            abortSignal
          ),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
      }
    );
  }

  getChargeOrderSearch(orderId: string) {
    return this.callApi(api => api.all("call_center").one("charge_orders", orderId).getEntity(ChargeOrderSearch));
  }

  refundPayment(payment: UnpairedPayment) {
    return this.callApi(api => api.all("call_center").one("unpaired_payments", payment.id).all("refund").postOnly({}));
  }

  pairPaymentWithOrder(payment: UnpairedPayment, order: ChargeOrderSearch) {
    return this.callApi(api =>
      api
        .all("call_center")
        .one("unpaired_payments", payment.id)
        .all("variable_symbol")
        .putOnly({ newVariableSymbol: order.orderIdent })
    );
  }
}
